import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// ─── Shared Logo / Nav ────────────────────────────────────────────────────────
function PricingNav() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-md border-b border-gray-100" : "bg-white border-b border-gray-100"}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#16a34a] rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-sun-line text-white text-sm"></i>
          </div>
          <span className="font-bold text-gray-900 text-lg tracking-tight whitespace-nowrap">
            SolarLead<span className="text-[#16a34a]">Source</span>
          </span>
        </a>
        <div className="flex items-center gap-3">
          <a href="/login" onClick={(e) => { e.preventDefault(); navigate("/login"); }} className="text-sm font-medium text-gray-600 hover:text-gray-900 transition cursor-pointer whitespace-nowrap">
            Log In
          </a>
          <button onClick={() => navigate("/signup")} className="bg-black text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-gray-900 transition active:scale-95 cursor-pointer whitespace-nowrap">
            Get Started
          </button>
        </div>
      </div>
    </nav>
  );
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const tiers = [
  {
    name: "Standard",
    priceRange: "$100–$130",
    scoreRange: "50–69",
    color: "border-gray-200",
    badge: "bg-gray-100 text-gray-600",
    iconBg: "bg-gray-50",
    iconColor: "text-gray-500",
    icon: "ri-user-line",
    highlight: false,
    description: "Entry-level leads with verified contact info. Good for volume buyers who want to maximize reach.",
    features: [
      "Verified name, phone & address",
      "Lead Score 50–69",
      "Solar Score 50–69",
      "Monthly bill $100–$199",
      "Timeline: 6–12 months",
      "Homeowner confirmed",
    ],
    bestFor: "High-volume teams testing new markets",
  },
  {
    name: "Quality",
    priceRange: "$130–$160",
    scoreRange: "70–84",
    color: "border-green-200",
    badge: "bg-green-100 text-green-700",
    iconBg: "bg-green-50",
    iconColor: "text-green-600",
    icon: "ri-user-star-line",
    highlight: true,
    description: "High-intent leads with strong solar potential. The sweet spot for most installers — great close rates at a fair price.",
    features: [
      "Verified name, phone & address",
      "Lead Score 70–84",
      "Solar Score 70–84",
      "Monthly bill $200–$299",
      "Timeline: 3–6 months",
      "Homeowner confirmed",
    ],
    bestFor: "Most installers — best balance of cost and close rate",
  },
  {
    name: "Premium",
    priceRange: "$160–$200",
    scoreRange: "85–99",
    color: "border-amber-200",
    badge: "bg-amber-100 text-amber-700",
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
    icon: "ri-vip-crown-line",
    highlight: false,
    description: "Top-tier leads with the highest solar potential and fastest timelines. For closers who want the best.",
    features: [
      "Verified name, phone & address",
      "Lead Score 85–99",
      "Solar Score 85–99",
      "Monthly bill $300+",
      "Timeline: 1–3 months",
      "Homeowner confirmed",
    ],
    bestFor: "Experienced closers targeting high-value installs",
  },
];

const scoreExplainer = [
  {
    name: "Lead Score",
    icon: "ri-user-star-line",
    color: "text-green-600",
    bg: "bg-green-50",
    border: "border-green-100",
    description: "Measures the quality and intent of the homeowner. Factors include how recently they submitted, how they responded to qualification questions, and their engagement level.",
    ranges: [
      { range: "50–69", label: "Interested", desc: "Submitted a form, basic qualification passed", color: "bg-gray-100 text-gray-600" },
      { range: "70–84", label: "High Intent", desc: "Actively seeking quotes, timeline within 6 months", color: "bg-green-100 text-green-700" },
      { range: "85–99", label: "Ready to Buy", desc: "Urgent timeline, multiple touchpoints, strong fit", color: "bg-amber-100 text-amber-700" },
    ],
  },
  {
    name: "Solar Score",
    icon: "ri-sun-line",
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-100",
    description: "Measures the property's solar potential. Factors include roof condition, sun exposure, monthly electricity bill, system size estimate, and federal incentive eligibility.",
    ranges: [
      { range: "50–69", label: "Viable", desc: "Meets basic solar criteria, moderate bill range", color: "bg-gray-100 text-gray-600" },
      { range: "70–84", label: "Strong Fit", desc: "Good roof, $200+ bill, solid incentive eligibility", color: "bg-green-100 text-green-700" },
      { range: "85–99", label: "Ideal Property", desc: "$300+ bill, excellent roof, maximum incentive potential", color: "bg-amber-100 text-amber-700" },
    ],
  },
];

const faqs = [
  {
    q: "Can I filter leads by score before buying?",
    a: "Yes. In your dashboard settings, you set a minimum Lead Score and Solar Score. Only leads meeting your threshold appear in your marketplace — you'll never see leads below your criteria.",
  },
  {
    q: "Is the price fixed or does it vary?",
    a: "Each lead has a fixed price set at the time it enters the marketplace. The price reflects the tier (Standard / Quality / Premium) and is visible before you reserve. No surprise charges.",
  },
  {
    q: "What happens if a lead's contact info is wrong?",
    a: "We stand behind our leads. If a phone number is disconnected or the address is invalid, contact support within 48 hours of purchase and we'll review it for a credit.",
  },
  {
    q: "Can I buy leads in bulk?",
    a: "Currently each lead is purchased individually through the marketplace. This ensures you review each lead's score and details before committing — no blind bulk packages.",
  },
  {
    q: "How does the 5-minute reservation work?",
    a: "When you click Reserve on a lead, it's locked for 5 minutes exclusively for you. If you complete checkout within that window, it's yours. If the timer expires, the lead returns to the marketplace.",
  },
  {
    q: "Are there any monthly fees?",
    a: "No. There are no subscription fees, monthly minimums, or setup costs. You only pay when you purchase a lead. Your dashboard access is completely free.",
  },
];

// ─── Components ───────────────────────────────────────────────────────────────
function AnimatedSection({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PricingPage() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <PricingNav />

      {/* Hero */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 uppercase tracking-wide">
            <i className="ri-price-tag-3-line"></i>
            Transparent Pricing
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-gray-900 tracking-tight leading-tight mb-5">
            Pay only for leads<br />
            <span className="text-[#16a34a]">that match your criteria</span>
          </h1>
          <p className="text-gray-500 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-8">
            No contracts. No monthly fees. No shared leads. Every lead is priced by tier based on score range — you see the price before you buy.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500">
            {[
              { icon: "ri-lock-line", label: "No contracts" },
              { icon: "ri-user-line", label: "1 lead = 1 installer" },
              { icon: "ri-secure-payment-line", label: "Stripe-secured" },
              { icon: "ri-dashboard-line", label: "Free dashboard" },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${b.icon} text-[#16a34a]`}></i>
                </div>
                <span>{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tier Cards */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Lead Tiers</h2>
              <p className="text-gray-500 max-w-lg mx-auto">Three tiers based on score range. Set your minimum in Settings — only matching leads appear in your marketplace.</p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((tier, i) => (
              <AnimatedSection key={tier.name} delay={i * 100}>
                <div className={`relative bg-white border-2 ${tier.color} rounded-2xl p-7 flex flex-col h-full ${tier.highlight ? "ring-2 ring-green-200" : ""}`}>
                  {tier.highlight && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#16a34a] text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                      Most Popular
                    </div>
                  )}

                  {/* Header */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-11 h-11 ${tier.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <i className={`${tier.icon} ${tier.iconColor} text-xl`}></i>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-gray-900 text-lg">{tier.name}</h3>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tier.badge}`}>Score {tier.scoreRange}</span>
                      </div>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-4">
                    <span className="text-3xl font-black text-gray-900">{tier.priceRange}</span>
                    <span className="text-gray-400 text-sm ml-1">/ lead</span>
                  </div>

                  <p className="text-gray-500 text-sm leading-relaxed mb-5">{tier.description}</p>

                  {/* Features */}
                  <ul className="flex flex-col gap-2.5 mb-6 flex-1">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-[#16a34a] text-sm font-bold"></i>
                        </div>
                        <span className="text-gray-600 text-sm">{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Best for */}
                  <div className="bg-gray-50 rounded-xl p-3 mb-5">
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Best for</p>
                    <p className="text-gray-700 text-sm font-medium">{tier.bestFor}</p>
                  </div>

                  <button
                    onClick={() => navigate("/signup")}
                    className={`w-full py-3 rounded-xl text-sm font-bold transition-all duration-200 active:scale-95 cursor-pointer whitespace-nowrap ${
                      tier.highlight
                        ? "bg-[#16a34a] text-white hover:bg-[#15803d]"
                        : "bg-gray-900 text-white hover:bg-black"
                    }`}
                  >
                    Get Started Free
                  </button>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Score Explainer */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wide">
                <i className="ri-bar-chart-line"></i>
                Scoring System
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">How scores are calculated</h2>
              <p className="text-gray-500 max-w-lg mx-auto">Every lead gets two independent scores. Understanding them helps you buy smarter.</p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {scoreExplainer.map((s, i) => (
              <AnimatedSection key={s.name} delay={i * 120}>
                <div className={`bg-white border ${s.border} rounded-2xl p-7`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <i className={`${s.icon} ${s.color} text-xl`}></i>
                    </div>
                    <h3 className="font-bold text-gray-900 text-lg">{s.name}</h3>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">{s.description}</p>
                  <div className="flex flex-col gap-2.5">
                    {s.ranges.map((r) => (
                      <div key={r.range} className="flex items-start gap-3">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0 ${r.color}`}>
                          {r.range}
                        </span>
                        <div>
                          <p className="text-gray-800 text-sm font-semibold leading-tight">{r.label}</p>
                          <p className="text-gray-400 text-xs mt-0.5">{r.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* What's always included */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Included with every lead</h2>
              <p className="text-gray-500 max-w-lg mx-auto">Regardless of tier, every purchase includes the same core data and platform access.</p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: "ri-user-line", title: "Full Contact Info", desc: "Name, phone number, and home address unlock immediately after payment." },
              { icon: "ri-map-pin-line", title: "Location Data", desc: "City, state, ZIP code, and service area confirmation included." },
              { icon: "ri-bar-chart-2-line", title: "Dual Score Report", desc: "Lead Score and Solar Score with breakdown of contributing factors." },
              { icon: "ri-home-line", title: "Property Details", desc: "Monthly bill range, roof condition, homeowner status, and system size estimate." },
              { icon: "ri-calendar-line", title: "Purchase Timeline", desc: "How soon the homeowner is looking to install — from 1 month to 12 months." },
              { icon: "ri-shield-check-line", title: "Exclusivity Guarantee", desc: "Once purchased, the lead is permanently removed from the marketplace." },
              { icon: "ri-dashboard-line", title: "Pipeline Tracker", desc: "Move leads through New → Contacted → Qualified → Appointment → Closed." },
              { icon: "ri-sticky-note-line", title: "Notes & Updates", desc: "Add private notes to each lead to track your outreach and follow-ups." },
              { icon: "ri-bank-card-line", title: "Billing History", desc: "Full transaction log with total spent, leads purchased, and avg cost per lead." },
            ].map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 60}>
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="w-9 h-9 bg-white border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#16a34a] text-base`}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                    <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <AnimatedSection>
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">How we compare</h2>
              <p className="text-gray-500 max-w-lg mx-auto">See how SolarLeadSource stacks up against traditional lead providers.</p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-5 py-4 text-gray-400 font-medium text-xs uppercase tracking-wide w-1/3">Feature</th>
                      <th className="px-5 py-4 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <div className="w-5 h-5 bg-[#16a34a] rounded flex items-center justify-center">
                            <i className="ri-sun-line text-white text-xs"></i>
                          </div>
                          <span className="font-bold text-gray-900 text-sm">SolarLeadSource</span>
                        </div>
                      </th>
                      <th className="px-5 py-4 text-center text-gray-400 font-medium text-sm">Shared Lead Platforms</th>
                      <th className="px-5 py-4 text-center text-gray-400 font-medium text-sm">Lead Aggregators</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {[
                      ["Exclusive leads", true, false, false],
                      ["Transparent pricing", true, false, false],
                      ["Score-based filtering", true, false, false],
                      ["No monthly contracts", true, false, true],
                      ["Pipeline CRM included", true, false, false],
                      ["5-min reservation system", true, false, false],
                      ["Instant contact unlock", true, true, false],
                      ["No setup fees", true, true, false],
                    ].map(([label, us, shared, agg]) => (
                      <tr key={label as string} className="hover:bg-gray-50 transition">
                        <td className="px-5 py-3.5 text-gray-700 font-medium">{label as string}</td>
                        <td className="px-5 py-3.5 text-center">
                          {us ? (
                            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                              <i className="ri-check-line text-[#16a34a] text-sm font-bold"></i>
                            </div>
                          ) : (
                            <div className="w-6 h-6 bg-red-50 rounded-full flex items-center justify-center mx-auto">
                              <i className="ri-close-line text-red-400 text-sm"></i>
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          {shared ? (
                            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                              <i className="ri-check-line text-[#16a34a] text-sm font-bold"></i>
                            </div>
                          ) : (
                            <div className="w-6 h-6 bg-red-50 rounded-full flex items-center justify-center mx-auto">
                              <i className="ri-close-line text-red-400 text-sm"></i>
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          {agg ? (
                            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                              <i className="ri-check-line text-[#16a34a] text-sm font-bold"></i>
                            </div>
                          ) : (
                            <div className="w-6 h-6 bg-red-50 rounded-full flex items-center justify-center mx-auto">
                              <i className="ri-close-line text-red-400 text-sm"></i>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <AnimatedSection>
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Pricing FAQ</h2>
              <p className="text-gray-500">Common questions about how pricing and lead tiers work.</p>
            </div>
          </AnimatedSection>

          <div className="flex flex-col gap-3">
            {faqs.map((faq, i) => (
              <AnimatedSection key={i} delay={i * 60}>
                <div className="bg-gray-50 border border-gray-100 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="font-semibold text-gray-900 text-sm pr-4">{faq.q}</span>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`text-gray-400 text-lg transition-transform duration-200 ${openFaq === i ? "ri-subtract-line" : "ri-add-line"}`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4">
                      <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-[#0A1F0F]">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
              Ready to start buying leads?
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
              Create your free account, set your score threshold, and browse the live marketplace — no credit card required to sign up.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate("/signup")}
                className="bg-[#16a34a] hover:bg-[#15803d] text-white font-bold px-8 py-4 rounded-full text-base transition-all duration-200 active:scale-95 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                Create Free Account
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
              <button
                onClick={() => navigate("/")}
                className="bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-8 py-4 rounded-full text-base transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Back to Homepage
              </button>
            </div>
            <p className="text-gray-500 text-sm mt-4">No contracts · No monthly fees · Pay per lead</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#16a34a] rounded-md flex items-center justify-center flex-shrink-0">
              <i className="ri-sun-line text-white text-xs"></i>
            </div>
            <span className="font-bold text-white text-base tracking-tight whitespace-nowrap">
              SolarLead<span className="text-[#16a34a]">Source</span>
            </span>
          </div>
          <p className="text-gray-600 text-sm">&copy; {new Date().getFullYear()} SolarLeadSource.com. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
